import {
  DateField,
  Field,
  NumberField,
  StringField,
} from '../../util/components/resource/resource-fields';
import { ApiResource } from '../api-resource';
import { Endpoint } from '../api.decorators';
import { Camp } from '../camp/camp.model';
import { Course } from '../course/course.model';
import { Customer } from '../customer/customer.model';
import { Workshop } from '../workshop/workshop.model';
import {
  DiscountEffectType,
  UnknownDiscountEffectTypeError,
} from './lib/discount.interfaces';
import { addYears } from 'date-fns';
import { Validators } from '@angular/forms';
import { CourseTheme } from '../course-theme/course-theme.model';
import { WorkshopTheme } from '../workshop-theme/workshop-theme.model';
import { Location } from '../location/location.model';

@Endpoint('discounts', 'Discount')
export class Discount extends ApiResource<Discount> {
  @StringField({
    formControlFactory: (fb) =>
      fb.control('', [Validators.required, Validators.minLength(6)]),
    label: $localize`Discount code`,
  })
  code!: string;

  @Field({
    formControlFactory: (fb) =>
      fb.control(DiscountEffectType.Flat, Validators.required),
    label: $localize`Effect type`,
    type: () => DiscountEffectType,
  })
  effectType!: DiscountEffectType;

  @NumberField({
    formControlFactory: (fb) => fb.control('', Validators.required),
    label: $localize`Effect amount`,
  })
  effectAmount!: number;

  @NumberField({ label: $localize`Remaining uses` })
  remainingUses!: number | null;

  @DateField({
    default: () => new Date(),
    label: $localize`Valid from`,
  })
  validFrom!: Date | null;

  @DateField({
    default: () => addYears(new Date(), 1),
    label: $localize`Valid until`,
  })
  validUntil!: Date | null;

  @Field({ type: () => Customer, label: $localize`Applicable customers` })
  applicableCustomers!: Customer[];

  @Field({ type: () => Camp, label: $localize`Applicable camps` })
  applicableCamps!: Camp[];

  @Field({ type: () => Course, label: $localize`Applicable courses` })
  applicableCourses!: Course[];

  @Field({ type: () => Workshop, label: $localize`Applicable workshops` })
  applicableWorkshops!: Workshop[];

  @Field({
    type: () => CourseTheme,
    label: $localize`Applicable course themes`,
  })
  applicableCourseThemes!: CourseTheme[];

  @Field({
    type: () => WorkshopTheme,
    label: $localize`Applicable workshop themes`,
  })
  applicableWorkshopThemes!: WorkshopTheme[];

  @Field({
    type: () => Location,
    label: $localize`Applicable locations`,
  })
  applicableLocations!: Location[];

  calculateDiscount(price: number) {
    switch (this.effectType) {
      case DiscountEffectType.Flat:
        return Math.max(0, Math.min(price, this.effectAmount));
      case DiscountEffectType.Percentage:
        return (price * this.effectAmount) / 100;
      default:
        throw new UnknownDiscountEffectTypeError(
          `'${this.effectType}' is not a valid discount effect type.`,
        );
    }
  }
}
